import { BlogCard } from './types';

export const metabolicMenopauseBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### **Metabolic health during menopause:** \n\n ## What Changes, What To Eat, And When To Get Tested',
      styles: {
        h2: 'font-mulish font-bold text-royal-875 mb-3 text-base md:text-lg leading-tight',
        h3: 'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight line-clamp-2',
        strong:
          'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        p: 'mb-0 mt-3',
      },
      topicsStyles: {
        h2: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 font-black leading-tight line-clamp-2',
        h3: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 pt-2 font-black',
        strong: 'font-mulish text-text-subtitle font-bold',
        p: 'font-mulish mb-0 text-2xl md:text-3xl p-2',
      },
    },
    description: {
      content:
        'Many women reach their 40s or 50s and notice something has shifted. Weight moves to the waist, energy becomes unpredictable, and losing weight feels harder than it ever did. These are not signs of failure. They reflect hormonal changes that affect how the body stores fat, regulates blood sugar and maintains muscle.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3 line-clamp-3 md:line-clamp-4',
      },
    },
    metaTitle:
      'Metabolic Health During Menopause: What Changes, What To Eat, And When To Get Tested',
    imageUrl: 'third-nutrition-blog-hero-image.webp',
    bannerUrl: 'third-nutrition-blog-hero-image.webp',
    slug: 'metabolic-health-during-menopause',
    contentType: 'article',
    speciality: 'Nutritional Therapy',
    iconUrl: 'monica-durigon.webp',
    doctorSlug: 'monica-durigon',
    date: {
      content: '07, October 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'DipION, mBANT, CNHC \n Nutritional Therapy Practitioner',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Ms Monica Durigon',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    bannerDescription: `
    Many women reach their 40s or 50s and notice something has shifted. Weight moves to the waist, energy becomes unpredictable, and losing weight feels harder than it ever did. These are not signs of failure. They reflect hormonal changes that affect how the body stores fat, regulates blood sugar and maintains muscle.\n\nLIPS Healthcare offers personalised metabolic and nutrition consultations at Battersea Power Station, London, helping women understand what is changing during perimenopause and menopause and how to respond with evidence-based support. This article explains what changes, why it matters and which steps make the most difference.`,
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What happens to metabolic health during menopause?',
          id: 'what-happens-to-metabolic-health-during-menopause',
        },
        {
          text: 'Why menopause moves fat to your waist',
          id: 'why-menopause-moves-fat-to-your-waist',
        },
        {
          text: 'Does menopause cause insulin resistance?',
          id: 'does-menopause-cause-insulin-resistance',
        },
        {
          text: 'What to eat for metabolic health during menopause',
          id: 'what-to-eat-for-metabolic-health-during-menopause',
        },
        {
          text: 'Should you get tested during menopause?',
          id: 'should-you-get-tested-during-menopause',
        },
        {
          text: 'Frequently asked questions about metabolic health during menopause',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

<span id="what-happens-to-metabolic-health-during-menopause"></span>

### What happens to metabolic health during menopause?

>Metabolic health refers to how well the body manages blood glucose, blood pressure, cholesterol, waist size and insulin sensitivity. During menopause, all of these can shift simultaneously as oestrogen levels decline, often before noticeable symptoms appear.

<div align="center">\n\n
![What happens to metabolism during menopause](third-nutrition-blog-first-image.webp "600x400")\n\n
</div>

The Study of Women's Health Across the Nation (SWAN) found that, around the menopausal transition, fat gain increased while lean mass began to decline, even when total body weight did not change significantly. Common changes include:

- Fat accumulating around the abdomen rather than the hips and thighs
- Lean muscle mass gradually reducing
- LDL cholesterol levels rising
- Insulin sensitivity decreasing in some women
- Blood pressure beginning to increase

The scales alone can be misleading. A woman may weigh the same as before yet have less muscle and more fat, with more of it stored around the abdomen. That shift in body composition carries real implications for long-term health.

<span id="why-menopause-moves-fat-to-your-waist"></span>

### Why menopause moves fat to your waist

Before menopause, women typically store more fat around the hips and thighs. As oestrogen declines, fat distribution shifts towards the abdomen, including visceral fat stored deeper around the abdominal organs.

<div align="center">\n\n
![What happens to metabolism during menopause](third-nutrition-blog-second-image.webp "600x400")\n\n
</div>

Visceral fat is more metabolically active than subcutaneous fat and is linked to insulin resistance, inflammation, cardiovascular disease and type 2 diabetes. Longitudinal research confirms that visceral fat increases across the menopausal transition, with the largest changes around the time of the final menstrual period.

Understanding this distinction changes the question from "how do I lose weight?" to "how do I support a healthier body composition?" Those are different goals, and they call for different strategies.

<span id="does-menopause-cause-insulin-resistance"></span>

### Does menopause cause insulin resistance?

Insulin resistance becomes more common during menopause, though it is not inevitable. It means cells become less responsive to insulin, the hormone that moves glucose from the bloodstream into cells. The body may then need to produce more insulin than before to maintain healthy blood sugar levels.

<div align="center">\n\n
![Nutrition plan for menopause metabolic health](third-nutrition-blog-third-image.webp "600x400")\n\n
</div>

No single factor is responsible. Several things that happen simultaneously during midlife can all contribute: 

- Increasing visceral fat 
- Gradual reduction in muscle mass 
- Reduced or less consistent physical activity 
- Disrupted or poor-quality sleep 
- Elevated or chronic stress 

Skeletal muscle is one of the primary tissues responsible for absorbing glucose after a meal. Preserving muscle during and after menopause is therefore central to metabolic protection, not just physical strength.
<div style="text-align:center;margin:2rem 0;">
<strong><a href="/specialities/nutritionist-london#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Book a metabolic health consultation</a></strong>
</div>

<span id="what-to-eat-for-metabolic-health-during-menopause"></span>

### What to eat for metabolic health during menopause

Supporting the body's metabolism during menopause does not require eliminating food groups. It requires building meals around the right combination of nutrients, consistently. A practical structure for each meal is:

<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:24px;margin:2rem auto;width:70%;align-items:stretch;">

  <div style="min-width:0;border:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0 4px 16px rgba(0,0,0,0.10);">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden rounded-xl [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-full [&_img]:!w-full [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:object-cover">
      <img src="third-nutrition-blog-protein-image.webp" alt="Protein-rich foods" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Protein</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Supports muscle maintenance, satiety and recovery. Spread intake across the day. Useful sources include eggs, Greek yoghurt, fish, chicken, beans, lentils and tofu.</p>
  </div>

  <div style="min-width:0;border:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0 4px 16px rgba(0,0,0,0.10);">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden rounded-xl [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-full [&_img]:!w-full [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:object-cover">
      <img src="third-nutrition-blog-carb-image.webp" alt="Fibre-rich vegetables and colourful plants" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Fibre-rich carbohydrates</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Slower-digesting options support steadier glucose regulation. Good choices include oats, quinoa, sweet potatoes, beans, lentils, whole fruit and wholegrain bread.</p>
  </div>

  <div style="min-width:0;border:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0 4px 16px rgba(0,0,0,0.10);">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden rounded-xl [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-full [&_img]:!w-full [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:object-cover">
      <img src="third-nutrition-blog-healty-fats-image.webp" alt="Healthy fats sources across the day" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Healthy fats</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Extra virgin olive oil, nuts, seeds, avocado and oily fish contribute to a balanced meal and support cardiovascular health alongside other markers.</p>
  </div>

  <div style="min-width:0;border:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0 4px 16px rgba(0,0,0,0.10);">
    <div class="mb-4 flex h-[240px] w-full items-center justify-center overflow-hidden rounded-xl [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-full [&_img]:!w-full [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:object-cover">
      <img src="third-nutrition-blog-plants-image.webp" alt="Plant variety and fermented foods" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Plant variety</h4>
    <p style="margin:0;font-size:14px;line-height:1.6;text-align:left;">Diversity across vegetables, beans and whole grains supports the gut microbiome, which plays a role in metabolic function. Fermented foods such as natural yoghurt or kefir can also be beneficial where well tolerated.</p>
  </div>

</div>

<span id="should-you-get-tested-during-menopause"></span>

### Should you get tested during menopause?

Blood tests can identify metabolic changes before symptoms appear. Depending on your history and individual risk, useful markers include:

- Fasting glucose and HbA1c, reflects average blood sugar over roughly three months 
- Full lipid profile; LDL, HDL, total cholesterol and triglycerides 
- Blood pressure 
- Waist circumference and body composition 
- Where indicated, additional markers assessing insulin sensitivity 

These results are most useful when read together as part of the full clinical picture rather than in isolation. 

<div align="center">\n\n
![When to get metabolic blood tests during menopause](third-nutrition-blog-fourth-image.webp "600x400")\n\n
</div>

Private blood tests, including same-day results for many panels, are available at LIPS Healthcare, Battersea Power Station. Patients come to us from Chelsea, Clapham, Vauxhall, Wandsworth, Pimlico and across South West London via the Northern line and London Overground.

><span style="font-weight: bold; color: #2A347A;">Medical disclaimer:</span>
>
>This article is for informational purposes only. It does not constitute medical advice and should not be used to diagnose or treat any medical condition. Always consult a qualified healthcare professional before making changes to your diet, exercise routine or health management plan.

If you experience severe symptoms, contact your GP immediately or call 999 in an emergency.

References
1. Greendale GA et al. Changes in body composition and weight during the menopause transition. JCI Insight. 2019;4(5):e124865.
2. Samargandy S et al. Abdominal visceral adipose tissue over the menopause transition. Menopause. 2021;28(6):626-633.
3. Juppi HK et al. Menopause and body composition: a complex field. Semin Reprod Med. 2025;43(2):85-105.
4. Gonçalves C et al. Systematic review of Mediterranean diet interventions in menopausal women. AIMS Public Health. 2024;11(1):110-129.
5. Chiavaroli L et al. Effect of low glycaemic index or load dietary patterns on glycaemic control. BMJ. 2021;374:n1651.
6. Karvonen-Gutierrez CA et al. Diabetes and menopause. Curr Diab Rep. 2016;16(4):20.
7. Department of Health and Social Care. UK Chief Medical Officers Physical Activity Guidelines. London: DHSC; 2019.

<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg pt-4 px-4 pb-2 my-8 text-sm text-text-default not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content: '#### More questions about menopause and metabolism?',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'What is metabolic health?',
          },
          answer: {
            content:
              'Metabolic health means the body manages blood glucose, blood pressure, cholesterol, waist size and insulin sensitivity within healthy ranges, without requiring medication to do so.',
          },
        },
        {
          question: {
            content: 'Does menopause automatically cause weight gain?',
          },
          answer: {
            content:
              'No. Menopause influences where fat is stored and how much lean muscle is retained, more than total body weight. Two women of the same age and menopause stage can have very different experiences. Ageing also contributes independently of hormonal changes.',
          },
        },
        {
          question: {
            content: 'What is the best diet during menopause for blood sugar?',
          },
          answer: {
            content:
              'Building meals around protein, fibre-rich carbohydrates, healthy fats and plant variety has the strongest evidence for supporting blood glucose regulation during menopause. Mediterranean-style dietary patterns specifically have been associated with improvements in cardiometabolic markers in published clinical reviews.',
          },
        },
        {
          question: {
            content:
              'Can blood tests detect metabolic problems before symptoms appear?',
          },
          answer: {
            content:
              'Yes. Fasting glucose, HbA1c, a full lipid profile and other markers can reveal early changes. Private blood tests are available at LIPS Healthcare, Battersea Power Station, with same-day results for many panels.',
          },
        },
        {
          question: {
            content: 'Does insulin resistance improve after menopause?',
          },
          answer: {
            content:
              'Not automatically. The most effective approach is consistent: resistance training, regular movement, adequate protein intake, a fibre-rich diet, quality sleep and stress management. A personalised plan works better than a generic protocol.',
          },
        },
        {
          question: {
            content:
              'When should I see a specialist about metabolic health during menopause?',
          },
          answer: {
            content:
              'If you have noticed changes in weight distribution, energy, cravings or blood sugar regulation, or if you have not had a metabolic blood panel in the last 12 months, a review is worth arranging. Early assessment leads to earlier and more effective intervention.',
          },
        },
      ],
    },
  },
];
